import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch { /* keep trying */ }
      if (attempts.current >= 10) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0c0c0d] p-6" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-3xl bg-[#141414] border border-white/10 p-10 text-center shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
        {status === "checking" && (<><Loader2 className="h-14 w-14 text-[#C9A227] mx-auto animate-spin" /><h1 className="mt-5 font-display text-2xl font-bold text-[#F3EDDC]">Confirming your payment…</h1><p className="mt-2 text-[#948C79]">This only takes a moment.</p></>)}
        {status === "paid" && (<><CheckCircle2 className="h-14 w-14 text-green-500 mx-auto" data-testid="payment-paid-icon" /><h1 className="mt-5 font-display text-2xl font-bold text-[#F3EDDC]">Payment successful!</h1><p className="mt-2 text-[#B7AF98]">{info?.package_name ? `Your ${info.package_name} order is confirmed.` : "Your order is confirmed."} We've created your project — complete onboarding to get started.</p><Button onClick={() => navigate("/portal")} className="mt-6 w-full rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12">Go to my portal <ArrowRight className="ml-2 h-4 w-4" /></Button></>)}
        {(status === "error" || status === "timeout") && (<><XCircle className="h-14 w-14 text-red-500 mx-auto" /><h1 className="mt-5 font-display text-2xl font-bold text-[#F3EDDC]">{status === "timeout" ? "Still processing" : "Payment issue"}</h1><p className="mt-2 text-[#B7AF98]">{status === "timeout" ? "Your payment is taking longer than expected. Check your portal shortly." : "We couldn't confirm your payment. Please try again."}</p><Button onClick={() => navigate("/pricing")} variant="outline" className="mt-6 w-full rounded-full h-12">Back to pricing</Button></>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0c0c0d] p-6" data-testid="payment-cancel-page">
      <div className="w-full max-w-md rounded-3xl bg-[#141414] border border-white/10 p-10 text-center">
        <XCircle className="h-14 w-14 text-[#7E7663] mx-auto" />
        <h1 className="mt-5 font-display text-2xl font-bold text-[#F3EDDC]">Checkout canceled</h1>
        <p className="mt-2 text-[#B7AF98]">No charge was made. You can pick a plan whenever you're ready.</p>
        <Button onClick={() => navigate("/pricing")} className="mt-6 w-full rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black h-12">Back to pricing</Button>
      </div>
    </div>
  );
}
